import Button, { ButtonProps } from 'antd/lib/button';
import Tooltip from 'antd/lib/tooltip';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { createAppSettingsAutomationsRouteWithAutomation } from '../../helpers/routeHelpers';
import { RootState } from '../../reducers/reducers';
import { selectAppStoreType, selectConfigFileLockForSection, selectOrg } from '../../selectors/selectors';
import { AppStoreType, AutomationType, BucketType, ConfigFileLock, ConfigFileSection } from '../../types';
import { automationDisplayNameForAutomationId } from '../../helpers/displayHelpers';
import { runwayColors } from '../styles/globalStyles';
import { useEffectiveAppId } from '@/utils/hooks/useUserCanPerformAction';

export const ConfigFileManagedMessage: React.FC<{ appId: string; lock: ConfigFileLock }> = ({ appId, lock }) => {
    const orgId = useSelector((state: RootState) => selectOrg(state)?.orgId);
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const automationName = automationDisplayNameForAutomationId(
        AutomationType.AutoApplyConfigFileFromVCS,
        appStoreType as AppStoreType,
    );

    return (
        <>
            Your organization is managing settings using Configuration as Code for this app. Edit your configuration
            file{' '}
            {lock.filePath && (
                <>
                    (<code>{lock.filePath}</code>){' '}
                </>
            )}
            in your repository, or turn off the{' '}
            <Link
                to={createAppSettingsAutomationsRouteWithAutomation(orgId, appId, lock.automationConfigId)}
                style={{ color: runwayColors.white, textDecoration: 'underline' }}
            >
                {automationName}
            </Link>{' '}
            automation.
        </>
    );
};

export const useConfigFileManagedMessage = (
    section: ConfigFileSection | undefined,
    appId?: string,
): React.ReactNode | undefined => {
    const effectiveAppId = useEffectiveAppId(appId);
    const lock = useSelector((state: RootState) => selectConfigFileLockForSection(state, effectiveAppId, section));
    return lock && effectiveAppId ? <ConfigFileManagedMessage appId={effectiveAppId} lock={lock} /> : undefined;
};

// A bucket's settings, members, notifications and automations; the file has no personal buckets
export const configFileSectionForBucket = (bucket: { type?: BucketType } | undefined): ConfigFileSection | undefined =>
    bucket?.type === BucketType.Personal ? undefined : ConfigFileSection.BuildDistroBuckets;

export const ConfigFileManagedButton: React.FC<
    ButtonProps & { configFileSection: ConfigFileSection; appId?: string }
> = ({ configFileSection, appId, disabled, ...rest }) => {
    const configFileMessage = useConfigFileManagedMessage(configFileSection, appId);
    return (
        <Tooltip title={configFileMessage} placement="topRight">
            <Button {...rest} disabled={disabled || !!configFileMessage} />
        </Tooltip>
    );
};
